import React, { MouseEvent, useState } from "react";

import { LinkHandler } from "@/components/common/LinkHandler";
import { RightArrowGreenIcon } from "@/components/icons/RightArrowGreenIcon";
import Picture from "@/components/ui-kit/Picture/Picture";
import { TextElementMen } from "@/components/ui-kit/TextElementMen/TextElementMen";
import { useNavigation } from "@/hooks/useNavigation";
import { GA4 } from "@/utils/ga4Utils";
import { cn, isIpad } from "@/utils/general";

export interface Subitem {
  name?: string;
  url: string;
  image: string;
  alt: string;
  text: string;
  subtext?: string;
}

interface MenNavigationScienceProps {
  item: { children: Subitem[] };
}

const removeActiveItemClass = (e: MouseEvent, preventClick?: boolean) => {
  const el = e.currentTarget as HTMLElement;
  const mouseLeftItem = hasHover(e);
  if (mouseLeftItem) {
    mouseLeftItem.classList.remove("hover");
  }
  if (preventClick) {
    e.preventDefault();
  }
  handleExpand(el);
};

const handleExpand = (el: HTMLElement) => {
  if (el?.id) {
    document.querySelectorAll("nav.navigation-variant [aria-expanded]").forEach((a) => {
      const isHover = a?.parentElement?.classList.contains("hover");
      (a as HTMLElement).setAttribute("aria-expanded", isHover ? "true" : "false");
    });
  }
};

const hasHover = (e: MouseEvent) => {
  return (e.target as HTMLElement)?.closest(".has-subnav.hover");
};

const buildCls = (subitem: Subitem) => {
  return subitem.subtext ? "has-subtext" : "no-subtext";
};

const trackClicked = (text: string, destination: string) => {
  GA4.onNavigationClicked({
    module_location: "navigation",
    type: "button",
    text,
    destination,
  });
};

const MenNavigationScience: React.FC<MenNavigationScienceProps> = ({ item }) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const navigate = useNavigation();

  const onTouchStart = (url: string) => {
    if (isIpad()) {
      navigate(url);
    }
  };

  return (
    <div className="dropdown dropdown-science-desktop absolute bg-nova-base-white w-max top-5 left-0 z-[12] border border-nova-transparency-black-20 shadow-navigation transition-opacity duration-100 ease-in-out">
      <div className="row bg-nova-base-white p-4 flex flex-row gap-[14px] flex-nowrap xl:gap-8 xl:p-10">
        {item.children.map((subitem, idx) => (
          <div
            key={`nav-item-${idx}`}
            className={cn("w-[200px] flex-1", subitem.name)}
            onTouchStart={() => onTouchStart(subitem.url)}
          >
            <LinkHandler
              path={subitem.url}
              onClick={() => {
                trackClicked(subitem.name, subitem.url);
              }}
            >
              <div
                className="w-full hover:bg-nova-base-solid-10 active:bg-nova-base-solid-20"
                onMouseEnter={() => setHoveredItem(subitem.name)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={(e) => removeActiveItemClass(e)}
              >
                <div
                  className={cn(
                    subitem.name.toLowerCase(),
                    buildCls(subitem),
                    "group",
                    hoveredItem === subitem.name && "hovered"
                  )}
                >
                  <div className="relative w-[200px] h-[200px]">
                    <Picture
                      src={subitem.image}
                      sources={[
                        {
                          media: "(min-width: 0px)",
                          srcSet: subitem.image,
                        },
                      ]}
                      alt={subitem.alt}
                      className="w-full h-full object-cover rounded-t-sm"
                      width={200}
                      height={200}
                    />
                  </div>
                  <div className="w-full border border-nova-transparency-black-20 rounded-b-sm border-t-0 p-2">
                    <div className="flex justify-between items-center">
                      <div>
                        <TextElementMen
                          element="h4"
                          text={subitem.name}
                          className="Title-10-Super-Normal uppercase"
                        />
                      </div>
                      <RightArrowGreenIcon />
                    </div>
                    <div className="pt-[4px]">
                      <TextElementMen
                        element="p"
                        text={subitem.text}
                        className="Caption-10-Dia-Normal text-nova-transparency-black-70"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </LinkHandler>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MenNavigationScience;
